import React, { useContext, useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import InnerLayout from "../../components/layouts/inner-layout";
import { DataContext } from "../../components/layouts/main-layout";
import InputField from "../../components/forms/input-field";
import SelectField from "../../components/forms/select-field";
import AddButton from "../../components/buttons/button";
import Pagination from "../../components/tables/pagination1";
import CloseIcon from "../../assets/icons/close-icon";
import EditIcon from "../../assets/icons/action-icons";
import DeleteIcon from "../../assets/icons/delete-icon";
import successToast from "../../components/helper-components/success-toast";
import errorToast from "../../components/helper-components/error-toast";
import {
  storeEmployeeListApi,
  storeEmployeeCreateApi,
  storeEmployeeUpdateApi,
  storeEmployeeDeleteApi,
  storeEmployeeDropdownApi,
} from "../../redux/apis/store-employee-api";

const headings = [
  { label: "EMPLOYEE CODE" },
  { label: "NAME" },
  { label: "DESIGNATION" },
  { label: "CONTACT NUMBER" },
  { label: "REPORTING TO" },
  { label: "ACTIONS" },
];

const headClass =
  "border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]";

const cellClass =
  "border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap";

// This backend answers with HTTP 200 even when the call fails, carrying the real
// outcome in the body as { status: false, message }. Axios resolves those, so
// without this check a failed save reports success with the error text inside a
// green toast. Treat an explicit status:false as a failure.
const failureMessage = (res) =>
  res?.data?.status === false ? res?.data?.message || "Request failed" : null;

const emptyEmployee = {
  employee_code: "",
  employee_name: "",
  employee_designation: "",
  employee_contact_number: "",
  reporting_to: "",
};

const schema = Yup.object().shape({
  employee_code: Yup.string().required("Employee code is required"),
  employee_name: Yup.string().required("Employee name is required"),
  employee_designation: Yup.string().required("Designation is required"),
  employee_contact_number: Yup.string()
    .required("Contact number is required")
    .matches(/^[0-9]{10}$/, "Contact number must be 10 digits"),
});

// The API returns the manager inline as reporting_to_details, so the name can be
// shown without resolving the id against the dropdown list.
const reportingToLabel = (item) => {
  const d = item?.reporting_to_details;
  if (!d) return item?.reporting_to ? String(item.reporting_to) : "--";
  return d.employee_name
    ? `${d.employee_name}${d.employee_code ? ` (${d.employee_code})` : ""}`
    : d.sem_id ?? "--";
};

const EmployeeMaster = () => {
  const { permissionFn } = useContext(DataContext);

  const [employees, setEmployees] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showPanel, setShowPanel] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reportingOptions, setReportingOptions] = useState([]);

  const fetchEmployees = () => {
    setLoading(true);
    storeEmployeeListApi({ pageNumber: currentPage, pageSize, search })
      .then((res) => {
        const failed = failureMessage(res);
        if (failed) {
          errorToast(failed);
          setEmployees([]);
          setDataCount(0);
          return;
        }
        const data = res.data?.data ?? {};
        const rows = Array.isArray(data.rows) ? data.rows : [];
        setEmployees(rows.filter((r) => r?.is_delete !== true));
        setDataCount(Number(data.totalCount ?? rows.length) || 0);
      })
      .catch((err) => {
        errorToast(err?.response?.data?.message ?? "Failed to load employees");
        setEmployees([]);
        setDataCount(0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, search]);

  const fetchReportingOptions = () => {
    // GET /store-employees/dropdown?pageNumber=1&pageSize=20
    storeEmployeeDropdownApi({ pageNumber: 1, pageSize: 20 })
      .then((res) => {
        const failed = failureMessage(res);
        if (failed) {
          // A silently empty picker looks the same as "no employees yet", so
          // surface why it could not be filled.
          errorToast(failed);
          setReportingOptions([]);
          return;
        }
        const rows = res.data?.data?.rows ?? [];
        setReportingOptions(
          rows
            .filter((r) => r?.sem_id)
            .map((r) => ({
              label: `${r.employee_name ?? r.sem_id}${
                r.employee_code ? ` (${r.employee_code})` : ""
              }`,
              value: r.sem_id,
            }))
        );
      })
      .catch((err) => {
        console.error(
          "Failed to fetch reporting managers:",
          err?.response?.data ?? err?.message
        );
        setReportingOptions([]);
      });
  };

  useEffect(() => {
    fetchReportingOptions();
  }, []);

  const closePanel = () => {
    setShowPanel(false);
    setEditing(null);
  };

  const handleDelete = (item) => {
    if (
      !window.confirm(
        `Delete ${item.employee_name ?? item.employee_code}? The record is kept for audit but stops appearing here.`
      )
    )
      return;
    storeEmployeeDeleteApi(item.sem_id)
      .then((res) => {
        const failed = failureMessage(res);
        if (failed) {
          errorToast(failed);
          return;
        }
        successToast(res.data?.message ?? "Employee deleted successfully");
        fetchEmployees();
        fetchReportingOptions();
      })
      .catch((err) => {
        errorToast(err?.response?.data?.message ?? "Failed to delete employee");
      });
  };

  const isEdit = !!editing;

  const initialValues = {
    employee_code: editing?.employee_code ?? "",
    employee_name: editing?.employee_name ?? "",
    employee_designation: editing?.employee_designation ?? "",
    employee_contact_number: editing?.employee_contact_number ?? "",
    reporting_to: editing?.reporting_to ?? "",
  };

  // An employee cannot report to themselves — the API rejects it, so the option
  // is removed rather than letting the save fail.
  const reportingOptionsFor = (current) =>
    reportingOptions.filter((o) => o.value !== current?.sem_id);

  const onSubmit = (values, { setSubmitting, resetForm }) => {
    const payload = {
      ...(isEdit ? { sem_id: editing.sem_id } : {}),
      employee_code: values.employee_code,
      employee_name: values.employee_name,
      employee_designation: values.employee_designation,
      employee_contact_number: values.employee_contact_number,
      reporting_to: values.reporting_to || null,
    };

    (isEdit ? storeEmployeeUpdateApi(payload) : storeEmployeeCreateApi(payload))
      .then((res) => {
        // A failed save must keep the panel open with the user's input intact,
        // so they can correct a duplicate code rather than retype everything.
        const failed = failureMessage(res);
        if (failed) {
          errorToast(failed);
          return;
        }
        successToast(
          res.data?.message ??
            (isEdit
              ? "Employee updated successfully"
              : "Employee created successfully")
        );
        fetchEmployees();
        fetchReportingOptions();
        closePanel();
        resetForm();
      })
      .catch((err) => {
        // The API's messages are specific — duplicate code, duplicate number,
        // self-reporting — so show them rather than a generic failure.
        errorToast(err?.response?.data?.message ?? "Failed to save employee");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <InnerLayout
      permissionFn={permissionFn}
      module="store_execution_process"
      title="Employee Master"
    >
      <div className="px-5 xl:px-10 pt-5 flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setCurrentPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Search by code or name"
            className="min-h-[42px] rounded-[8px] px-3 border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--input-color)] text-[length:var(--input-font-size)] focus:outline-none focus:border-[var(--input-focus)] w-[18rem]"
          />
          {permissionFn("store_execution_process")?.create && (
            <AddButton
              text="Add Employee"
              onclkFn={() => {
                setEditing(null);
                setShowPanel(true);
              }}
              prefixIcon={true}
            />
          )}
        </div>

        <div className="tab-container outline outline-[var(--list-table-border-color)] outline-1 border-radius overflow-auto">
          <table className="w-full border-collapse mt-0">
            <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
              <tr className="h-[50px]">
                {headings.map((h, i) => (
                  <th key={i} className={headClass}>
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={headings.length}
                    className="px-[8px] h-[50px] text-center text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td
                    colSpan={headings.length}
                    className="px-[8px] h-[50px] text-center text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]"
                  >
                    {search ? "No employees match that search." : "No employees found."}
                  </td>
                </tr>
              ) : (
                employees.map((item, idx) => (
                  <tr
                    key={item.sem_id ?? idx}
                    className="hover:bg-[var(--list-table-hover-bg)]"
                  >
                    <td className={cellClass}>{item.employee_code ?? "--"}</td>
                    <td className={cellClass}>{item.employee_name ?? "--"}</td>
                    <td className={cellClass}>{item.employee_designation ?? "--"}</td>
                    <td className={cellClass}>{item.employee_contact_number ?? "--"}</td>
                    <td className={cellClass}>{reportingToLabel(item)}</td>
                    <td className={cellClass}>
                      <div className="flex items-center gap-3">
                        {permissionFn("store_execution_process")?.update && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(item);
                              setShowPanel(true);
                            }}
                          >
                            <EditIcon />
                          </button>
                        )}
                        {permissionFn("store_execution_process")?.delete && (
                          <DeleteIcon onClickFn={() => handleDelete(item)} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          paginationProps={{
            pageSize,
            dataCount,
            setCurrentPage,
            currentPage,
            setPageSize,
          }}
        />
      </div>

      {/* Add / Edit — right slide panel */}
      <Transition.Root show={showPanel} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closePanel}>
          <div className="fixed inset-0 overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
            </Transition.Child>

            <div className="fixed inset-0 flex justify-end">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="h-full relative w-[420px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
                  <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={schema}
                    onSubmit={onSubmit}
                  >
                    {({ isSubmitting }) => (
                      <Form className="flex flex-col h-full">
                        <div
                          className="h-[61px] bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b flex-shrink-0"
                          style={{ borderColor: "var(--slide-panel-border)" }}
                        >
                          <span
                            style={{
                              color: "var(--slide-panel-h-fg)",
                              fontSize: "var(--slide-panel-h-fs)",
                              fontWeight: "var(--slide-panel-h-fw)",
                            }}
                          >
                            {isEdit ? "Edit Employee" : "Add Employee"}
                          </span>
                          <button type="button" onClick={closePanel}>
                            <CloseIcon />
                          </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-5 py-5">
                          <InputField
                            name="employee_code"
                            label="Employee Code"
                            placeholder="Enter Employee Code"
                            isRequired={true}
                            labelWidth="9.5rem"
                          />
                          <InputField
                            name="employee_name"
                            label="Employee Name"
                            placeholder="Enter Employee Name"
                            isRequired={true}
                            labelWidth="9.5rem"
                          />
                          <InputField
                            name="employee_designation"
                            label="Designation"
                            placeholder="Enter Designation"
                            isRequired={true}
                            labelWidth="9.5rem"
                          />
                          <InputField
                            name="employee_contact_number"
                            label="Contact Number"
                            placeholder="Enter Contact Number"
                            isRequired={true}
                            maxLength={10}
                            labelWidth="9.5rem"
                          />
                          <SelectField
                            name="reporting_to"
                            id="reporting_to"
                            label="Reporting To"
                            placeholder="Select Reporting Manager"
                            options={reportingOptionsFor(editing)}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth="9.5rem"
                            errMsgContainerClass="ps-2"
                          />

                          <p className="text-[length:var(--input-place-font-size)] text-[var(--input-place-color)] mt-3 leading-relaxed">
                            Employee Code and Contact Number must each be unique.
                            An employee cannot report to themselves.
                          </p>
                        </div>

                        <div className="px-5 py-4 border-t flex justify-end flex-shrink-0">
                          <AddButton
                            prefixIcon={false}
                            text={isEdit ? "Update" : "Save"}
                            type="submit"
                            loading={isSubmitting}
                          />
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </InnerLayout>
  );
};

export default EmployeeMaster;
